import RGBColor from 'classes/rgb-color';
import HEXColor from 'classes/hex-color';
import { DocumentCategory, Review, StatisticByStatus } from './review-category';
import { ReactNode } from 'react';

export enum FailureReason {
  missingAnnotation = 'missingAnnotation',
  notPresentedAnnotation = 'notPresentedAnnotation',
}

export enum ReviewType {
  font = 'font',
  safety = 'safety',
  branding = 'branding',
  claims = 'claims',
  claimsOrder = 'claimsOrder',
}

export enum ReviewCharacteristic {
  fontType = 'fontType',
  fontColor = 'fontColor',
  fontSize = 'fontSize',
  ssiCheck = 'ssiCheck',
  ssiAnnotation = 'ssiAnnotation',
  ssiApplicability = 'ssiApplicability',
  ssiPresence = 'ssiPresence',
  productName = 'productName',
  alignedClaimText = 'alignedClaimText',
  claimsDependentContent = 'claimsDependentContent',
  claimsOrder = 'claimsOrder',
  claimStatus = 'claimStatus',
  claimText = 'claimText',
}

export enum ReviewResult {
  fail = 'fail',
  pass = 'pass',
  skip = 'skip',
  warning = 'warning',
}

export const ReviewResultList: ReviewResult[] = [
  ReviewResult.fail,
  ReviewResult.pass,
  ReviewResult.skip,
  ReviewResult.warning,
];

export enum SplitMode {
  split = 'split',
  undivided = 'undivided',
}

export enum ColorSuggestionValueKeys {
  hex = 'hex',
  rgb = 'rgb',
}

export enum ValueKeys {
  fontFamily = 'fontFamily',
  size = 'size',
  ssiCheck = 'ssiCheck',
  ssiAnnotation = 'ssiAnnotation',
  brandingType = 'brandingType',
  ssiPresence = 'ssiPresence',
  claimText = 'claimText',
  alignedClaimText = 'alignedClaimText',
  claimStatus = 'claimStatus',
}

export enum PageGenericErrorCode {
  NO_TEXT_DATA_FOUND = 'NO_TEXT_DATA_FOUND',
  PAGE_IS_IMAGE = 'PAGE_IS_IMAGE',
}

export type PageGenericError = {
  code: PageGenericErrorCode;
  message: string;
};

export enum ReviewCategoryErrorCode {
  NO_SSI_ANNOTATIONS = 'NO_SSI_ANNOTATIONS',
  PAGE_IS_IMAGE_STYLE = 'PAGE_IS_IMAGE_STYLE',
  PAGE_IS_IMAGE_SAFETY = 'PAGE_IS_IMAGE_SAFETY',
  PAGE_IS_IMAGE_BRANDING = 'PAGE_IS_IMAGE_BRANDING',
  PAGE_IS_IMAGE_CLAIMS = 'PAGE_IS_IMAGE_CLAIMS',
  PAGE_IS_IMAGE_CLAIMS_ERROR = 'PAGE_IS_IMAGE_CLAIMS_ERROR',

  // deprecated, PM-5129
  CLAIMS_UNSUPPORTED_DOCUMENT_PRODUCT = 'CLAIMS_UNSUPPORTED_DOCUMENT_PRODUCT',
  // deprecated, PM-5129
  CLAIMS_UNSUPPORTED_DOCUMENT_BRAND = 'CLAIMS_UNSUPPORTED_DOCUMENT_BRAND',

  CLAIMS_UNSUPPORTED_DOCUMENT = 'CLAIMS_UNSUPPORTED_DOCUMENT',
  SAFETY_REVIEW_UNSUPPORTED_DOCUMENT = 'SAFETY_REVIEW_UNSUPPORTED_DOCUMENT',
  STYLE_REVIEW_UNSUPPORTED_DOCUMENT = 'STYLE_REVIEW_UNSUPPORTED_DOCUMENT',
  STYLE_REVIEW_MULTI_PRODUCT_UNSUPPORTED = 'STYLE_REVIEW_MULTI_PRODUCT_UNSUPPORTED',
}

export type ReviewCategoryError = {
  code: ReviewCategoryErrorCode;
  message: string;
};

export const safetyBlockers: ReviewCategoryErrorCode[] = [
  ReviewCategoryErrorCode.SAFETY_REVIEW_UNSUPPORTED_DOCUMENT,
];

export const styleBlockers: ReviewCategoryErrorCode[] = [
  ReviewCategoryErrorCode.STYLE_REVIEW_UNSUPPORTED_DOCUMENT,
  ReviewCategoryErrorCode.STYLE_REVIEW_MULTI_PRODUCT_UNSUPPORTED,
];

export const claimsBlockers: ReviewCategoryErrorCode[] = [
  ReviewCategoryErrorCode.CLAIMS_UNSUPPORTED_DOCUMENT_PRODUCT,
  ReviewCategoryErrorCode.CLAIMS_UNSUPPORTED_DOCUMENT_BRAND,
  ReviewCategoryErrorCode.CLAIMS_UNSUPPORTED_DOCUMENT,
];

export const coloredErrors: ReviewCategoryErrorCode[] = [
  /*
    this array must be empty
    as we switched to gray-only messages
    PM-4840
  */
];

export const SuggestionValueKeys = {
  ...ColorSuggestionValueKeys,
  ...ValueKeys,
};

export type SuggestionValue = {
  [SuggestionValueKeys.fontFamily]?: string;
  [SuggestionValueKeys.hex]?: HEXColor;
  [SuggestionValueKeys.rgb]?: RGBColor;
  [SuggestionValueKeys.size]?: number;
  [SuggestionValueKeys.ssiCheck]?: string;
  [SuggestionValueKeys.ssiAnnotation]?: string;
  [SuggestionValueKeys.brandingType]?: string;
  [SuggestionValueKeys.ssiPresence]?: string;
  [SuggestionValueKeys.claimText]?: string;
  [SuggestionValueKeys.alignedClaimText]?: string;
  [SuggestionValueKeys.claimStatus]?: string;
};

export const coloredCheckMessages = ['SSI_TEXT_IS_IMAGE', 'CLAIM_IS_IMAGE'];

export enum CheckMessageLevel {
  DOCUMENT = 'DOCUMENT',
  PAGE = 'PAGE',
  GROUP = 'GROUP',
  UNIT = 'UNIT',
}

export enum DifferenceType {
  ADD = 'ADD',
  REMOVE = 'REMOVE',
  SAME = 'SAME',
}

export const wildCardPatternRegexp = /{\*}+/;

export type CheckMessage = {
  code: string;
  message: string;
  type: 'ERROR' | 'WARNING' | 'BUCKET';
  level: CheckMessageLevel;
  pageNumber?: number;
  priority?: number;
};

export type ClaimNumber = {
  number: string;
  type: 'CLAIM_NUMBER' | 'ORDER_NUMBER';
  groupNumber?: string;
};

export type CheckMissingClaims = {
  claimNumber: ClaimNumber;
  claimType: string;
  claimText: string;
  alignedClaimText: string;
};

export type Check = {
  reviewType: ReviewType;
  characteristic: ReviewCharacteristic;
  result: ReviewResult;
  failureReason?: FailureReason;
  messages?: (string | CheckMessage)[];
  usedValues?: SuggestionValue[];
  requiredValues?: SuggestionValue[];
  claimsOrderUsedValues?: ClaimNumber[];
  claimsOrderRequiredValues?: ClaimNumber[];
  missingClaims?: CheckMissingClaims[];
  isExceptionGranted?: boolean;
  textDifference?: TextDifference[];
  ssiInstruction?: string;
  ssiText?: string;
  ssiUnitVersions?: {
    type: string;
    value: string[];
  };
  label?: string;
  note?: ReactNode;
};

export type MetadataCheck = {
  result: ReviewResult;
  type: string;
  used?: string;
  required?: string;
};

export type CheckPayload = {
  reviewType: ReviewType;
  characteristic: ReviewCharacteristic;
  result: ReviewResult;
  failureReason?: FailureReason;
  messages?: (string | CheckMessage)[];
  usedValues?: ClaimNumber[] | SuggestionValue[];
  requiredValues?: ClaimNumber[] | SuggestionValue[];
  missingClaims?: CheckMissingClaims[];
  textDifference?: TextDifference[];
  ssiInstruction?: string;
  ssiText?: string;
};

export type SsiApplicability = {
  reviewType: ReviewType;
  characteristic: ReviewCharacteristic;
  messages: string;
  ssiInstruction: string;
  ssiText: string;
};

export type SsiCompleteness = {
  checks: SsiCompletenessCheck[];
  messages: CheckMessage[];
  result: ReviewResult;
};

export type SsiCompletenessCheck = {
  messages: (string | CheckMessage)[];
  ssiInstruction: string;
  ssiText: string;
};

export type SsiDocument = {
  name: string;
  documentNumber: string;
  productFamily: string;
};

export enum SsiWarningSafety {
  LINKED_SSI_DOCUMENT_IS_UNSUPPORTED = 'LINKED_SSI_DOCUMENT_IS_UNSUPPORTED',
  LINKED_SSI_DOCUMENT_IS_MISSED = 'LINKED_SSI_DOCUMENT_IS_MISSED',
  SSI_CHECK_FOR_UNBRANDED_IS_UNSUPPORTED = 'SSI_CHECK_FOR_UNBRANDED_IS_UNSUPPORTED',
}

export enum SsiWarningStyle {
  STYLE_REVIEW_FOR_RESOURCE_TYPE_IS_UNSUPPORTED = 'STYLE_REVIEW_FOR_RESOURCE_TYPE_IS_UNSUPPORTED',
}

export type DocumentReviewsPayload = {
  [DocumentCategory.brandingReview]: {
    checks: CheckPayload[];
    result: ReviewResult;
  };
  [DocumentCategory.ssiCompleteness]: {
    checks: SsiCompletenessCheck[];
    messages: CheckMessage[];
    result: ReviewResult;
  };
  [DocumentCategory.metadata]: {
    checks?: MetadataCheck[];
  };
};

export type DocumentAlertsPayload = {
  [Review.style]: StatisticByStatus;
  [Review.safety]: StatisticByStatus;
  [Review.claims]: StatisticByStatus;
  [DocumentCategory.ssiCompleteness]: StatisticByStatus;
};

export type CMYK = {
  cyan: number;
  magenta: number;
  yellow: number;
  key: number;
};

export type RGB = {
  red: number;
  green: number;
  blue: number;
};

export type TextDifference = {
  type: DifferenceType;
  text: string;
};
